import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface OpsDashboardContextType {
  // Refresh triggers for each tab
  refreshTriggers: {
    supply: number;
    demand: number;
    utilization: number;
    assignments: number;
    overview: number;
    admin: number;
  };
  
  // Methods to trigger refresh for specific tabs
  refreshSupplyView: () => void;
  refreshDemandView: () => void;
  refreshUtilizationView: () => void;
  refreshAssignmentMap: () => void;
  refreshOverview: () => void;
  refreshAdminPanel: () => void;
  
  // Method to refresh all tabs at once
  refreshAllTabs: () => void;
  
  // Method to refresh tabs that are affected by slot assignments
  refreshAfterSlotAssignment: () => void;
}

const OpsDashboardContext = createContext<OpsDashboardContextType | undefined>(undefined);

interface OpsDashboardProviderProps {
  children: ReactNode;
}

export const OpsDashboardProvider: React.FC<OpsDashboardProviderProps> = ({ children }) => {
  const [refreshTriggers, setRefreshTriggers] = useState({
    supply: 0,
    demand: 0,
    utilization: 0,
    assignments: 0,
    overview: 0,
    admin: 0,
  });

  const refreshSupplyView = useCallback(() => {
    setRefreshTriggers(prev => ({ ...prev, supply: prev.supply + 1 }));
  }, []);

  const refreshDemandView = useCallback(() => {
    setRefreshTriggers(prev => ({ ...prev, demand: prev.demand + 1 }));
  }, []);

  const refreshUtilizationView = useCallback(() => {
    setRefreshTriggers(prev => ({ ...prev, utilization: prev.utilization + 1 }));
  }, []);

  const refreshAssignmentMap = useCallback(() => {
    setRefreshTriggers(prev => ({ ...prev, assignments: prev.assignments + 1 }));
  }, []);

  const refreshOverview = useCallback(() => {
    setRefreshTriggers(prev => ({ ...prev, overview: prev.overview + 1 }));
  }, []);

  const refreshAdminPanel = useCallback(() => {
    setRefreshTriggers(prev => ({ ...prev, admin: prev.admin + 1 }));
  }, []);

  const refreshAllTabs = useCallback(() => {
    setRefreshTriggers(prev => ({
      supply: prev.supply + 1,
      demand: prev.demand + 1,
      utilization: prev.utilization + 1,
      assignments: prev.assignments + 1,
      overview: prev.overview + 1,
      admin: prev.admin + 1,
    }));
  }, []);

  const refreshAfterSlotAssignment = useCallback(() => {
    // After slot assignments, we need to refresh:
    // - Supply View (to show updated slot assignments and utilization)
    // - Assignment Map (to show the new assignments)
    // - Utilization Analysis (to show updated revenue and utilization metrics)
    // - Overview (to show updated summary metrics)
    // - Demand View (to show updated preference signals and assignments)
    // - Admin Panel (to show updated show and slot counts)
    setRefreshTriggers(prev => ({
      supply: prev.supply + 1,
      demand: prev.demand + 1,
      utilization: prev.utilization + 1,
      assignments: prev.assignments + 1,
      overview: prev.overview + 1,
      admin: prev.admin + 1,
    }));
  }, []);

  const value: OpsDashboardContextType = {
    refreshTriggers,
    refreshSupplyView,
    refreshDemandView,
    refreshUtilizationView,
    refreshAssignmentMap,
    refreshOverview,
    refreshAdminPanel,
    refreshAllTabs,
    refreshAfterSlotAssignment,
  };

  return (
    <OpsDashboardContext.Provider value={value}>
      {children}
    </OpsDashboardContext.Provider>
  );
};

export const useOpsDashboard = (): OpsDashboardContextType => {
  const context = useContext(OpsDashboardContext);
  if (context === undefined) {
    throw new Error('useOpsDashboard must be used within an OpsDashboardProvider');
  }
  return context;
};

export default OpsDashboardContext;